import React, { type ReactNode } from 'react';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { toEnvironmentCategoryTypeFilter } from '../../common/utils/environment-type-filter.tsx';
import { useDeepMemo } from '../../common/utils/misc';
import { useTimeWindowFilter } from '../../controllers/date-filter/utils';
import { useTableFilterStore } from '../../controllers/top-filter';
import { CoreDataFilterProvider, CoreDataServiceProvider } from '../../services/core-data/main.tsx';
import type { Filter } from '../../services/core-data/types.tsx';
import { ProjectKeyContext, useProjectAnalyticsAttributes } from '../../services/get-project';
import {
	useEnvironmentTypesFilter,
	useIssueConfigurationPreference,
} from '../../services/my-preference/main.tsx';

type Props = {
	projectKey: ProjectKey;
	children: ReactNode;
};

/**
 * Returns null if useEnvironmentTypesFilter is not ready yet.
 */
const useCoreDataFilter = (projectKey: ProjectKey): Filter | null => {
	const [filterStore] = useTableFilterStore();
	const timeWindow = useTimeWindowFilter();
	const environmentTypesFilter = useEnvironmentTypesFilter();
	const issueConfigPreference = useIssueConfigurationPreference();

	return useDeepMemo<Filter | null>(
		environmentTypesFilter === null || issueConfigPreference == null
			? null
			: {
					projectKey,
					text: filterStore.text,
					assignees: filterStore.assignees,
					timeWindow,
					epicIds: filterStore.epicIds,
					issueTypeIds: filterStore.issueTypeIds,
					environments: {
						names: filterStore.environmentNames,
						types: environmentTypesFilter,
						categories: toEnvironmentCategoryTypeFilter(filterStore.environmentTypeConcatNames),
					},
					fixVersions: filterStore.fixVersions,
					showIssuesWithoutDeployments: issueConfigPreference.showIssuesWithoutDeployments,
				},
	);
};

const CoreDataProvider = ({ children, projectKey }: Props) => {
	const filter = useCoreDataFilter(projectKey);
	return (
		<CoreDataFilterProvider filter={filter}>
			<CoreDataServiceProvider>{children}</CoreDataServiceProvider>
		</CoreDataFilterProvider>
	);
};

const AnalyticsContextProvider = ({ children }: { children: ReactNode }) => {
	const projectInfo = useProjectAnalyticsAttributes('deploymentsPage');
	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="deploymentsPage"
			containerType="project"
			containerId={projectInfo?.projectId}
			attributes={{ ...projectInfo }}
		>
			{children}
		</ContextualAnalyticsData>
	);
};

export const Providers = (
	{ children, projectKey }: Props, // Do not add any hooks here because re-rendering ContextualAnalyticsData
) => (
	// will cause the whole tree to re-render.
	<ProjectKeyContext.Provider value={projectKey}>
		<AnalyticsContextProvider>
			<CoreDataProvider projectKey={projectKey}>{children}</CoreDataProvider>
		</AnalyticsContextProvider>
	</ProjectKeyContext.Provider>
);
